import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone } from '@angular/core';
import moment from 'moment';
import { BehaviorSubject, fromEvent, Observable, Subject } from 'rxjs';
import { DisplayDecorator } from '../../decorators/display-decorator';
import { EventInterface } from '../../interfaces/event.interface';
import { EquipmentStatus } from '../../models/complex/confirmation-equipment.model';
import { MaterialModel } from '../../models/low-level/material.model';
import { TextModel } from '../../models/primitive/text.model';
import { NotificationCompletedListModel } from '../../models/low-level/notification-completed.model';

@Injectable({
    providedIn: 'root',
})
export class EventService implements EventInterface {
    private appInitializationCompletedSubject = new BehaviorSubject(false);
    private equipmentStatusInArrivalChangedSubject: Subject<EquipmentStatus>;
    private notificationsUpdatedSubject: Subject<boolean | undefined>;
    private complaintOptionSelectedChangedSubject: Subject<boolean | undefined>;
    private materialBillableOptionChangedSubject: Subject<boolean>;
    private materialBillingIndicatorsNotReceivedSubject: Subject<MaterialModel[]>;
    private timeRecordingBillableOptionChangedSubject: Subject<boolean>;
    private timeRecordingValidationChangedSubject: Subject<void>;
    private syncReceiveCompletedSubject: Subject<void>;
    private materialsUpdatedSubject: Subject<void>;
    private materialConsumptionOrNotReceivedChangedSubject: Subject<MaterialModel[]>;
    private whatsNewItemClickedSubject: Subject<void>;
    private bootStockSetSyncCompletedSubject: Subject<void>;
    private followupOptionChangedSubject: Subject<boolean>;
    private phoneFixSelectedSubject: Subject<boolean>;
    private equipmentStatusAfterFixChangedSubject: Subject<DisplayDecorator<TextModel>>;
    private encryptionInitializedSubject: Subject<void>;
    private cordovaBackSubject: Subject<void>;
    private cordovaResumeSubject: Subject<void>;
    private refreshMaterialReturnCountSubject: Subject<void>;
    private updateTimeOfRecoveryMaxValueSubject: Subject<moment.Moment>;
    private notificationRefreshRequiredSubject: Subject<boolean>;
    private triggerSPQDocRefreshSubject: Subject<void>;
    private triggerMaterialPickedSubject: Subject<void>;
    public newUpdatesSyncRequiredSubject: Subject<void>;
    public triggerUpdateNotificationCountOnSuccessfulSyncSubject: Subject<void>;
    public triggerTimesheetDataSubject: Subject<void>;
    private requestSynchronization: Subject<void>;
    public startWorkUpdatedSubject: Subject<boolean>;
    private completedNotifications: BehaviorSubject<NotificationCompletedListModel>;
    private appointmentSlotBooked: BehaviorSubject<boolean>;

    public appInitializationCompleted$ = this.appInitializationCompletedSubject.asObservable();
    public triggerTimesheetDataSync$: Observable<void>;
    public triggerUpdateNotificationCountOnSuccessfulSync$: Observable<void>;
    public cordovaBack$: Observable<void>;
    public cordovaResume$: Observable<void>;
    public notificationsUpdated$: Observable<boolean | undefined>;
    public complaintOptionSelectedChanged$: Observable<boolean | undefined>;
    public materialBillableOptionChanged$: Observable<boolean>;
    public materialBillingIndicatorsNotReceived$: Observable<MaterialModel[]>;
    public timeRecordingBillableOptionChanged$: Observable<boolean>;
    public timeRecordingValidationChanged$: Observable<void>;
    public syncReceiveCompleted$: Observable<void>;
    public materialsUpdated$: Observable<void>;
    public materialConsumptionOrNotReceivedChanged$: Observable<MaterialModel[]>;
    public whatsNewItemClicked$: Observable<void>;
    public bootStockSetSyncCompleted$: Observable<void>;
    public followupOptionChanged$: Observable<boolean>;
    public phoneFixSelected$: Observable<boolean>;
    public equipmentStatusAfterFixChanged$: Observable<DisplayDecorator<TextModel>>;
    public encryptionInitialized$: Observable<void>;
    public equipmentStatusInArrivalChanged$: Observable<EquipmentStatus>;
    public refreshMaterialReturnCount$: Observable<void>;
    public updateTimeOfRecoveryMaxValue$: Observable<moment.Moment>;
    public notificationRefreshRequired$: Observable<boolean>;
    public newUpdatesSyncRequired$: Observable<void>;
    public triggerSPQDocRefresh$: Observable<void>;
    public triggerMaterialPicked$: Observable<void>;
    public startWorkUpdated$: Observable<boolean>;
    public synchronizationRequested$: Observable<void>;
    public completedNotifications$: Observable<NotificationCompletedListModel>;
    public appointmentSlotBooked$: Observable<boolean>;

    public constructor(
        @Inject(DOCUMENT) protected readonly document: Document,
        protected readonly zone: NgZone,
    ) {
        // "regular" observables
        this.notificationsUpdatedSubject = new Subject<boolean | undefined>();
        this.complaintOptionSelectedChangedSubject = new Subject<boolean>();
        this.materialBillableOptionChangedSubject = new Subject<boolean>();
        this.materialBillingIndicatorsNotReceivedSubject = new Subject<MaterialModel[]>();
        this.timeRecordingBillableOptionChangedSubject = new Subject<boolean>();
        this.timeRecordingValidationChangedSubject = new Subject<void>();
        this.syncReceiveCompletedSubject = new Subject<void>();
        this.newUpdatesSyncRequiredSubject = new Subject<void>();
        this.materialsUpdatedSubject = new Subject<void>();
        this.materialConsumptionOrNotReceivedChangedSubject = new Subject<MaterialModel[]>();
        this.whatsNewItemClickedSubject = new Subject<void>();
        this.bootStockSetSyncCompletedSubject = new Subject<void>();
        this.followupOptionChangedSubject = new Subject<boolean>();
        this.phoneFixSelectedSubject = new Subject<boolean>();
        this.equipmentStatusAfterFixChangedSubject = new Subject<DisplayDecorator<TextModel>>();
        this.encryptionInitializedSubject = new Subject<void>();
        this.equipmentStatusInArrivalChangedSubject = new Subject<EquipmentStatus>();
        this.refreshMaterialReturnCountSubject = new Subject<void>();
        this.updateTimeOfRecoveryMaxValueSubject = new Subject<moment.Moment>();
        this.notificationRefreshRequiredSubject = new Subject<boolean>();
        this.triggerSPQDocRefreshSubject = new Subject<void>();
        this.triggerMaterialPickedSubject = new Subject<void>();
        this.triggerUpdateNotificationCountOnSuccessfulSyncSubject = new Subject<void>();
        this.triggerTimesheetDataSubject = new Subject<void>();
        this.requestSynchronization = new Subject<void>();
        this.startWorkUpdatedSubject = new Subject<boolean>();
        this.completedNotifications = new BehaviorSubject<NotificationCompletedListModel>(
            new NotificationCompletedListModel(),
        );
        this.appointmentSlotBooked = new BehaviorSubject<boolean>(false);

        this.cordovaBackSubject = new Subject<void>();
        fromEvent(document, 'backbutton').subscribe(() => this.zone.run(() => this.cordovaBackSubject.next()));
        this.cordovaBack$ = this.cordovaBackSubject.asObservable();

        // Cord ova device events when app resumes
        this.cordovaResumeSubject = new Subject<void>();
        fromEvent(document, 'resume').subscribe(() => this.zone.run(() => this.cordovaResumeSubject.next()));

        this.cordovaResume$ = this.cordovaResumeSubject.asObservable();

        this.notificationsUpdated$ = this.notificationsUpdatedSubject.asObservable();
        this.complaintOptionSelectedChanged$ = this.complaintOptionSelectedChangedSubject.asObservable();
        this.materialBillableOptionChanged$ = this.materialBillableOptionChangedSubject.asObservable();
        this.materialBillingIndicatorsNotReceived$ = this.materialBillingIndicatorsNotReceivedSubject.asObservable();
        this.timeRecordingBillableOptionChanged$ = this.timeRecordingBillableOptionChangedSubject.asObservable();
        this.timeRecordingValidationChanged$ = this.timeRecordingValidationChangedSubject.asObservable();
        this.syncReceiveCompleted$ = this.syncReceiveCompletedSubject.asObservable();
        this.newUpdatesSyncRequired$ = this.newUpdatesSyncRequiredSubject.asObservable();
        this.materialsUpdated$ = this.materialsUpdatedSubject.asObservable();
        this.materialConsumptionOrNotReceivedChanged$ =
            this.materialConsumptionOrNotReceivedChangedSubject.asObservable();
        this.whatsNewItemClicked$ = this.whatsNewItemClickedSubject.asObservable();
        this.bootStockSetSyncCompleted$ = this.bootStockSetSyncCompletedSubject.asObservable();
        this.followupOptionChanged$ = this.followupOptionChangedSubject.asObservable();
        this.phoneFixSelected$ = this.phoneFixSelectedSubject.asObservable();
        this.equipmentStatusAfterFixChanged$ = this.equipmentStatusAfterFixChangedSubject.asObservable();
        this.encryptionInitialized$ = this.encryptionInitializedSubject.asObservable();
        this.equipmentStatusInArrivalChanged$ = this.equipmentStatusInArrivalChangedSubject.asObservable();
        this.refreshMaterialReturnCount$ = this.refreshMaterialReturnCountSubject.asObservable();
        this.updateTimeOfRecoveryMaxValue$ = this.updateTimeOfRecoveryMaxValueSubject.asObservable();
        this.notificationRefreshRequired$ = this.notificationRefreshRequiredSubject.asObservable();
        this.triggerSPQDocRefresh$ = this.triggerSPQDocRefreshSubject.asObservable();
        this.triggerMaterialPicked$ = this.triggerMaterialPickedSubject.asObservable();
        this.triggerUpdateNotificationCountOnSuccessfulSync$ =
            this.triggerUpdateNotificationCountOnSuccessfulSyncSubject.asObservable();
        this.triggerTimesheetDataSync$ = this.triggerTimesheetDataSubject.asObservable();
        this.synchronizationRequested$ = this.requestSynchronization.asObservable();
        this.startWorkUpdated$ = this.startWorkUpdatedSubject.asObservable();
        this.completedNotifications$ = this.completedNotifications.asObservable();
        this.appointmentSlotBooked$ = this.appointmentSlotBooked.asObservable();
    }

    public triggerAppInitializationComplete(): void {
        this.appInitializationCompletedSubject.next(true);
    }

    // A ch ange in the Status of an assignment should be triggered
    public triggerNotificationsUpdated(notificationWasCancelled?: boolean): void {
        setTimeout(() => this.notificationsUpdatedSubject.next(notificationWasCancelled));
    }

    // A ch ange in the selection of an complaint option should be triggered
    public triggerComplaintOptionSelectionChanged(selectionChanged: boolean): void {
        this.complaintOptionSelectedChangedSubject.next(selectionChanged);
    }

    // A ch ange in the selection of an Material billable option should be triggered
    public triggerMaterialBillableOptionChanged(isMaterialBillable: boolean): void {
        this.materialBillableOptionChangedSubject.next(isMaterialBillable);
    }

    // A ch ange in the selection of an Time recording Billable Option should be triggered
    public triggerTimeRecordingBillableOptionChanged(isTimeRecordingEntryBillable: boolean): void {
        this.timeRecordingBillableOptionChangedSubject.next(isTimeRecordingEntryBillable);
    }

    // A ch ange in the time recording entries validation should be triggered
    public triggerTimeRecordingValidationChanged(): void {
        setTimeout(() => this.timeRecordingValidationChangedSubject.next());
    }

    // Publ ish the SYNC_RECEIVE_COMPLETED event.
    public triggerSyncReceiveCompleted(): void {
        setTimeout(() => this.syncReceiveCompletedSubject.next());
    }

    public triggerNewUpdatesRequired(): void {
        setTimeout(() => this.newUpdatesSyncRequiredSubject.next());
    }

    public triggerMaterialsUpdated(): void {
        setTimeout(() => this.materialsUpdatedSubject.next());
    }

    // A ch ange in the selection of an equipment should be triggered
    public triggerMaterialConsumptionOrNotReceivedChanged(materials: MaterialModel[]): void {
        this.materialConsumptionOrNotReceivedChangedSubject.next(materials);
    }

    public triggerWhatsNewItemClicked(): void {
        setTimeout(() => this.whatsNewItemClickedSubject.next());
    }

    public triggerBootStockSetSyncCompleted(): void {
        setTimeout(() => this.bootStockSetSyncCompletedSubject.next());
    }

    public triggerFollowupOptionChanged(followUpOptionYes: boolean): void {
        this.followupOptionChangedSubject.next(followUpOptionYes);
    }

    public triggerPhoneFixSelected(isPhoneFixSelected: boolean): void {
        this.phoneFixSelectedSubject.next(isPhoneFixSelected);
    }

    public triggerEquipmentStatusAfterFixChanged(statusAfterFix: DisplayDecorator<TextModel>): void {
        this.equipmentStatusAfterFixChangedSubject.next(statusAfterFix);
    }

    public triggerEncryptionInitialized(): void {
        setTimeout(() => this.encryptionInitializedSubject.next());
    }

    public triggerEquipmentStatusInArrivalChanged(equipmentStatus: EquipmentStatus): void {
        this.equipmentStatusInArrivalChangedSubject.next(equipmentStatus);
    }

    public triggerRefreshMaterialReturnCount(): void {
        setTimeout(() => this.refreshMaterialReturnCountSubject.next());
    }

    public triggerUpdateTimeOfRecoveryMaxValue(maxValue: moment.Moment): void {
        setTimeout(() => this.updateTimeOfRecoveryMaxValueSubject.next(maxValue));
    }

    public triggerNotificationRefreshRequiredChanged(refresh: boolean): void {
        this.notificationRefreshRequiredSubject.next(refresh);
    }

    public triggerSPQDocRefresh(): void {
        this.triggerSPQDocRefreshSubject.next();
    }

    public triggerMaterialPicked(): void {
        this.triggerMaterialPickedSubject.next();
    }

    public triggerMaterialBillingIndicatorsNotReceived(materials: MaterialModel[]): void {
        this.materialBillingIndicatorsNotReceivedSubject.next(materials);
    }

    public triggerUpdateNotificationCountOnSuccessfulSync(): void {
        this.triggerUpdateNotificationCountOnSuccessfulSyncSubject.next();
    }

    public triggerUpdateTimesheet(): void {
        this.triggerTimesheetDataSubject.next();
    }

    public triggerSynchronization(): void {
        this.requestSynchronization.next();
    }

    public triggerStartWorkUpdated(status: boolean): void {
        this.startWorkUpdatedSubject.next(status);
    }

    public updateCompletedNotifications(recentlyCompleted: NotificationCompletedListModel): void {
        this.completedNotifications.next(recentlyCompleted);
    }

    public triggerAppointmentSlotBooked(isSlotBooked: boolean): void {
        this.appointmentSlotBooked.next(isSlotBooked);
    }
}
